import {
  flush as flushAmplitude,
  getDeviceId as getDeviceIdAmplitude,
  identify as identifyAmplitude,
  Identify,
  init as initAmplitude,
  add as addAmplitudePlugin,
} from '@amplitude/analytics-browser'

import config from '@config'
import checkout from '@lib/analytics/amplitude/checkout'
import common from '@lib/analytics/amplitude/common'
import confirmation from '@lib/analytics/amplitude/confirmation'
import cookies from '@lib/analytics/amplitude/cookies'
import error from '@lib/analytics/amplitude/error'
import help from '@lib/analytics/amplitude/help'
import helpers from '@lib/analytics/amplitude/helpers'
import landing from '@lib/analytics/amplitude/landing'
import manageBooking from '@lib/analytics/amplitude/manageBooking'
import featureExperiments from '@lib/analytics/amplitude/plugins/features'
import sessionReplays from '@lib/analytics/amplitude/plugins/sessionReplays'
// import webExperiments from '@lib/analytics/amplitude/plugins/webExperiments'
import popups from '@lib/analytics/amplitude/popups'
import results from '@lib/analytics/amplitude/results'
import paramsUtils from '@lib/params'

const { sendEvent } = helpers

interface ValueChangeEvent<T> {
  from: T
  to: T
}

const parseDomain = (url: string): { domain: string; subdomain: string } | null => {
  try {
    const { hostname } = new URL(url)
    const parts = hostname.split('.')
    // istanbul ignore else
    if (parts[parts.length - 1] === 'localhost') parts.push('')

    return {
      domain: parts.slice(-2).join('.'),
      subdomain: parts.slice(0, -2).join('.'),
    }
  } catch {
    return null
  }
}
// We need to manually identify the user for a new subdomain within the same domain
// https://distribusion.atlassian.net/browse/OWL-2935?focusedCommentId=187363
const isDifferentSubdomain = () => {
  const referrerUrl = parseDomain(document.referrer)
  const currentUrl = parseDomain(window.location.href)
  if (!referrerUrl || !currentUrl) return false
  return referrerUrl.domain === currentUrl.domain && referrerUrl.subdomain !== currentUrl.subdomain
}

interface IdentifyData {
  set?: Record<string, string>
  append?: Record<string, string>
}
const identify = (data: IdentifyData): void => {
  const identifyEvent = new Identify()

  data.set && Object.entries(data.set).forEach(([key, value]) => identifyEvent.set(key, value))
  data.append && Object.entries(data.append).forEach(([key, value]) => identifyEvent.append(key, value))

  identifyAmplitude(identifyEvent)
}

const identifyUtm = () => {
  const utmParams = paramsUtils.getUtmParams(window.location.search)

  // istanbul ignore else
  if (Object.entries(utmParams).length > 0) {
    identify({ set: utmParams })
  }
}

const init = ({ container_id, sessionReplay }: Analytics.IdConfig): void => {
  if (!container_id) return

  const urlParams = new URLSearchParams(window.location.search)
  const deviceId = urlParams.get('deviceId')

  if (isDifferentSubdomain()) {
    identifyUtm()
  }

  const sessionReplayPluginInstance = sessionReplays.getPlugin({ sessionReplay })

  addAmplitudePlugin(sessionReplayPluginInstance)

  initAmplitude(container_id.toString(), undefined, {
    transport: 'beacon',
    deviceId: deviceId || undefined,
    flushIntervalMillis: config.env === 'test' ? 0 : 1000,
    defaultTracking: {
      attribution: true,
      pageViews: false,
      sessions: true,
      fileDownloads: false,
      formInteractions: false,
    },
  })

  // TODO: Uncomment web experiments when performance / security risks are known
  // https://distribusion.atlassian.net/browse/BUFFER-195
  // webExperiments.init({
  //   amplitudeApiKey: container_id.toString(),
  // })

  featureExperiments.init({
    amplitudeApiKey: container_id.toString(),
  })

  addEventListener('beforeunload', () => {
    flushAmplitude()
  })
}
const getDeviceId = (): string | undefined => getDeviceIdAmplitude()
const changeLocale = (props: ValueChangeEvent<Locale>): void => {
  sendEvent('change-language', props)
}
const changeCurrency = (props: ValueChangeEvent<Currency>): void => {
  sendEvent('change-currency', props)
}

interface PopularDirectionProps {
  departureLocation: Location.NamedItem
  arrivalLocation: Location.NamedItem
  position: number
}

const clickPopularDirection = ({ departureLocation, arrivalLocation, position }: PopularDirectionProps): void => {
  sendEvent('click-popular-directions', {
    departure: { type: departureLocation.type, code: departureLocation.code },
    arrival: { type: arrivalLocation.type, code: arrivalLocation.code },
    route: `${departureLocation.name} - ${arrivalLocation.name}`,
    position,
  })
}

const viewSearchPage = (): void => {
  sendEvent('view-homepage', {}, { measurePerformance: 'page' })
}

const clickWhatsappButton = (): void => {
  sendEvent('whatsapp-click')
}

const onInternalRedirect = (): void => {
  performance.mark('redirect')
}

export default {
  init,
  changeLocale,
  changeCurrency,
  viewSearchPage,
  clickPopularDirection,
  landing,
  checkout,
  confirmation,
  error,
  results,
  common,
  getDeviceId,
  onInternalRedirect,
  clickWhatsappButton,
  identify,
  cookies,
  manageBooking,
  help,
  popups,
}
